import { availableLanguages } from "../../../constans/languagesList";
import { Project } from "../../../types/Company";
import {
  ProjectsAvailableSessionTypes,
  TypeNewProject,
} from "../../../types/Projects";
import { Label, SpecialistLevel, SpecialistTag } from "../../../types/TypeSpecialist";
import { areAllTextFieldsFilled } from "../../../utils/validations";

export type ProjectFormModel = {
  projectData: TypeNewProject;
  existingVideos: Project["video"];
  existingTags: SpecialistTag[];
};

export type ProjectSubmitValidation = {
  isValid: boolean;
  missingFields: string[];
};

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

export const createEmptyMultiLangStrings = (): Label[] =>
  availableLanguages.map((language) => ({
    text: "",
    language,
  }));

export const getInitialProjectFormData = (
  currentDate: Date = new Date(),
): TypeNewProject => ({
  title: createEmptyMultiLangStrings(),
  description: createEmptyMultiLangStrings(),
  briefDescription: createEmptyMultiLangStrings(),
  adminRemarkForExperts: createEmptyMultiLangStrings(),
  isPrivate: true,
  isPublicAccess: false,
  creationDate: currentDate,
  endDate: addDays(currentDate, 7),
  clientLimit: 1,
  specialistLimit: 1,
  treeQuizTagsToSkip: [],
  availableSessionTypes: undefined,
  isBarter: false,
  skipSpecialistRecordConfirmation: false,
  specializations: undefined,
  pricing: [],
  packagePricing: [],
  skipSpecialistConfirmation: false,
  enableTwilioSms: true,
  allowSpecialistLeave: true,
  allowSpecialistPause: true,
  staticPackagePrices: false,
  staticSessionPrices: false,
  sessionTax: undefined,
  packageTax: undefined,
  offerPromoPackages: false,
  quizBuckets: [],
});

export const getProjectLevelId = (
  level: string | SpecialistLevel | { _id?: string } | undefined,
) => (typeof level === "string" ? level : (level?._id ?? ""));

export const getProjectSpecializationId = (
  specialization: string | { _id?: string } | undefined,
) =>
  typeof specialization === "string" ? specialization : (specialization?._id ?? "");

const mapPricingToFormData = (
  pricing: Project["pricing"],
): TypeNewProject["pricing"] =>
  pricing?.map((item) => ({
    ...item,
    level: getProjectLevelId(item.level),
  }));

export const mapProjectToFormData = (project: Project): TypeNewProject => {
  const rescheduleOptions = project.rescheduleOptions;

  return {
    title: project.title,
    description: project.description,
    briefDescription:
      project.briefDescription && project.briefDescription.length
        ? project.briefDescription
        : createEmptyMultiLangStrings(),
    adminRemarkForExperts:
      project.adminRemarkForExperts ?? createEmptyMultiLangStrings(),
    isPrivate: project.isPrivate,
    isPublicAccess: project.isPublicAccess ?? false,
    creationDate: project.creationDate
      ? new Date(project.creationDate)
      : undefined,
    endDate: project.endDate ? new Date(project.endDate) : undefined,
    clientLimit: project.clientLimit,
    specialistLimit: project.specialistLimit,
    availableSessionTypes: project.availableSessionTypes?.map((item) => ({
      type: item.type,
      amount: item.amount,
      unlimited: item.unlimited,
      specialization: getProjectSpecializationId(
        item.specialization ??
          (item as unknown as { level?: SpecialistLevel })?.level
            ?.mainSpecialization,
      ),
    })),
    specializations: (Array.isArray(project.specializations)
      ? typeof project.specializations[0] === "string"
        ? project.specializations
        : project.specializations.map((item: { _id: string }) => item._id)
      : []) as string[],
    isBarter: project.isBarter,
    pricing: mapPricingToFormData(project.pricing),
    packagePricing: project.packagePricing,
    clientCancelThreshold: rescheduleOptions?.clientCancelThreshold,
    specialistCancelThreshold: rescheduleOptions?.specialistCancelThreshold,
    clientRescheduleThreshold: rescheduleOptions?.clientRescheduleThreshold,
    clientCancelPackageThreshold: rescheduleOptions?.clientCancelPackageThreshold,
    clientReschedulePackageThreshold:
      rescheduleOptions?.clientReschedulePackageThreshold,
    clientReschedulePackageAmount:
      rescheduleOptions?.clientReschedulePackageAmount,
    skipSpecialistConfirmation: project.skipSpecialistConfirmation ?? false,
    enableTwilioSms: project.enableTwilioSms,
    allowSpecialistLeave: project.allowSpecialistLeave,
    allowSpecialistPause: project.allowSpecialistPause,
    skipSpecialistRecordConfirmation:
      project.skipSpecialistRecordConfirmation ?? false,
    staticPackagePrices: project.staticPackagePrices ?? false,
    staticSessionPrices: project.staticSessionPrices ?? false,
    sessionTax: project.sessionTax,
    packageTax: project.packageTax,
    offerPromoPackages: project.offerPromoPackages ?? false,
    treeQuizTagsToSkip: project.treeQuizTagsToSkip?.map((item) => item._id!),
    quizBuckets: project.quizBuckets?.map((bucket) => ({
      title: bucket.title.map((item) => ({ ...item })),
      activationTime: bucket.activationTime,
      quizes: [...bucket.quizes],
    })) ?? [],
  };
};

export const createProjectFormModel = (project?: Project): ProjectFormModel => ({
  projectData: project ? mapProjectToFormData(project) : getInitialProjectFormData(),
  existingVideos: project?.video,
  existingTags: project?.tags ?? [],
});

export const getSelectedProjectTypes = (
  projectData: TypeNewProject,
): ProjectsAvailableSessionTypes[] =>
  Array.from(
    new Set(projectData.availableSessionTypes?.map((item) => item.type)),
  );

export const formatDateForProjectInput = (value: Date | string) => {
  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const validateProjectSubmit = (
  projectData: TypeNewProject,
): ProjectSubmitValidation => {
  const missingFields: string[] = [];
  const selectedProjectTypes = getSelectedProjectTypes(projectData);

  if (!areAllTextFieldsFilled(projectData.title)) {
    missingFields.push("Project title");
  }

  if (!areAllTextFieldsFilled(projectData.description)) {
    missingFields.push("Project description");
  }

  if (!projectData.clientLimit || projectData.clientLimit <= 0) {
    missingFields.push("Client limit");
  }

  if (!projectData.specialistLimit || projectData.specialistLimit <= 0) {
    missingFields.push("Specialist limit");
  }

  if (!projectData.availableSessionTypes || !selectedProjectTypes.length) {
    missingFields.push("Project type");
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};
